import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit, Trash2, Calendar, Clock, User, Building2, Phone, Mail, ArrowLeft } from 'lucide-react';
import { appointmentsAPI } from '../../lib/api.js';
import { useCompanies } from '../../hooks/useSharedData.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { formatDate, formatDateTime } from '../../utils/formatters.js';

// Helper function to format phone number for display (show only 10 digits)
const formatPhoneForDisplay = (phoneNumber) => {
  if (!phoneNumber) return 'Not provided';
  
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // If it's 12 digits and starts with 91, show only the last 10 digits
  if (digitsOnly.length === 12 && digitsOnly.startsWith('91')) {
    return digitsOnly.substring(2);
  }
  
  // If it's 10 digits, show as is
  if (digitsOnly.length === 10) {
    return digitsOnly;
  }
  
  // For other cases, return the original
  return phoneNumber;
};

function AppointmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [rescheduleData, setRescheduleData] = useState({
    appointment_date: '',
    appointment_time: '',
  });

  const queryClient = useQueryClient();

  // Fetch appointment details
  const { data: appointment, isLoading } = useQuery({
    queryKey: ['appointment', id],
    queryFn: () => appointmentsAPI.getById(id),
  });

  // Fetch companies using shared hooks
  const { data: companies } = useCompanies();

  // Mutations
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => appointmentsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['appointment', id]);
      queryClient.invalidateQueries(['appointments']);
      setShowEditModal(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => appointmentsAPI.delete(id),
    onSuccess: () => {
      navigate('/appointments');
    },
  });

  const rescheduleMutation = useMutation({
    mutationFn: ({ id, data }) => appointmentsAPI.reschedule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['appointment', id]);
      queryClient.invalidateQueries(['appointments']);
      setShowRescheduleModal(false);
    },
  });

  const appointmentData = appointment?.data?.appointment;

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'scheduled':
        return 'warning';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'danger';
      case 'rescheduled':
        return 'info';
      default:
        return 'default';
    }
  };

  const getRequestTypeBadgeVariant = (type) => {
    switch (type) {
      case 'leave_conversion':
        return 'warning';
      case 'leave_remaining':
        return 'info';
      case 'other':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleEdit = () => {
    setFormData({
      first_name: appointmentData.first_name || '',
      last_name: appointmentData.last_name || '',
      full_name: appointmentData.full_name || '',
      badge_number: appointmentData.badge_number || '',
      contact_number: appointmentData.contact_number || '',
      phone_number: appointmentData.phone_number || '',
      email: appointmentData.email || '',
      request_type: appointmentData.request_type || '',
      appointment_date: appointmentData.appointment_date ? appointmentData.appointment_date.split('T')[0] : '',
      appointment_time: appointmentData.appointment_time || '',
      company_id: appointmentData.company_id || '',
      purpose: appointmentData.purpose || '',
      remarks: appointmentData.remarks || '',
    });
    setShowEditModal(true);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate({ id, data: formData });
  };

  const handleReschedule = (e) => {
    e.preventDefault();
    rescheduleMutation.mutate({ id, data: rescheduleData });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (!appointmentData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 dark:text-gray-400">Appointment not found</p>
        <Button onClick={() => navigate('/appointments')} className="mt-4">
          Back to Appointments
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Appointment Details
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Badge: {appointmentData.badge_number}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleEdit} className="flex items-center gap-2">
            <Edit size={16} />
            Edit
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowRescheduleModal(true)}
            className="flex items-center gap-2"
          >
            <Calendar size={16} />
            Reschedule
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            className="flex items-center gap-2"
          >
            <Trash2 size={16} />
            Delete
          </Button>
        </div>
      </div>

      {/* Appointment Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Information */}
        <Card>
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Personal Information
          </h2>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <User size={16} className="text-gray-400" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Full Name</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {appointmentData.first_name && appointmentData.last_name 
                    ? `${appointmentData.first_name} ${appointmentData.last_name}`
                    : appointmentData.full_name || 'Not provided'
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Building2 size={16} className="text-gray-400" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Company</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {appointmentData.company_name || 'Not assigned'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone size={16} className="text-gray-400" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Phone Number</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {formatPhoneForDisplay(appointmentData.phone_number || appointmentData.contact_number)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail size={16} className="text-gray-400" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {appointmentData.email || 'Not provided'}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Appointment Information */}
        <Card>
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Appointment Information
          </h2>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Calendar size={16} className="text-gray-400" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Date</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {formatDate(appointmentData.appointment_date)}
                </p>
              </div>
            </div>
            {appointmentData.appointment_time && (
              <div className="flex items-center gap-3">
                <Clock size={16} className="text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Time</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {appointmentData.appointment_time}
                  </p>
                </div>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Request Type</p>
              <Badge variant={getRequestTypeBadgeVariant(appointmentData.request_type)} className="mt-1">
                {appointmentData.request_type}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
              <Badge variant={getStatusBadgeVariant(appointmentData.status)} className="mt-1">
                {appointmentData.status}
              </Badge>
            </div>
          </div>
        </Card>
      </div>

      {/* Additional Information */}
      <Card>
        <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
          Additional Information
        </h2>
        <div className="space-y-4">
          {appointmentData.purpose && (
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Purpose</p>
              <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                {appointmentData.purpose}
              </p>
            </div>
          )}
          {appointmentData.remarks && (
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Remarks</p>
              <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                {appointmentData.remarks}
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Created At</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {formatDateTime(appointmentData.created_at)}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
              Edit Appointment
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  required
                />
                <Input
                  label="Last Name"
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  required
                />
                <Input
                  label="Badge Number"
                  value={formData.badge_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, badge_number: e.target.value }))}
                  required
                />
                <select
                  value={formData.company_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_id: e.target.value }))}
                  className="input-field"
                  required
                >
                  <option value="">Select Company</option>
                  {companies?.data?.data?.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
                <Input
                  label="Contact Number"
                  value={formData.contact_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_number: e.target.value }))}
                />
                <Input
                  label="Phone Number"
                  value={formData.phone_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                  placeholder="Enter phone number"
                />
                <Input
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                />
                <select
                  value={formData.request_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, request_type: e.target.value }))}
                  className="input-field"
                  required
                >
                  <option value="">Select Request Type</option>
                  <option value="leave_conversion">Leave Conversion</option>
                  <option value="leave_remaining">Leave Remaining</option>
                  <option value="other">Other</option>
                </select>
                <Input
                  label="Appointment Date"
                  type="date"
                  value={formData.appointment_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, appointment_date: e.target.value }))}
                  required
                />
                <Input
                  label="Appointment Time"
                  type="time"
                  value={formData.appointment_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, appointment_time: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Purpose
                </label>
                <textarea
                  value={formData.purpose}
                  onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Remarks
                </label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateMutation.isPending}
                >
                  Update
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
              Reschedule Appointment
            </h2>
            <form onSubmit={handleReschedule} className="space-y-4">
              <Input
                label="New Date"
                type="date"
                value={rescheduleData.appointment_date}
                onChange={(e) => setRescheduleData(prev => ({ ...prev, appointment_date: e.target.value }))}
                required
              />
              <Input
                label="New Time"
                type="time"
                value={rescheduleData.appointment_time}
                onChange={(e) => setRescheduleData(prev => ({ ...prev, appointment_time: e.target.value }))}
              />
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowRescheduleModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={rescheduleMutation.isPending}
                >
                  Reschedule
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppointmentDetailPage;
